import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { employmentLabel, formatSalary } from "@/lib/jobs";
import {
  MapPin, Briefcase, Sparkles, Send, Loader2, X, CheckCircle2, BadgeCheck,
  Flame, Edit3, Trash2, Globe, ArrowLeft,
} from "lucide-react";

export default function JobView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [j, setJ] = useState(null);
  const [err, setErr] = useState("");
  const [applyOpen, setApplyOpen] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);
  const [tiers, setTiers] = useState({});

  const load = async () => {
    try { const { data } = await api.get(`/jobs/${id}`); setJ(data); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(()=>{ load(); }, [id]);

  useEffect(() => {
    api.get("/promotions/tiers").then(r => setTiers(r.data || {})).catch(()=>{});
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("boost") === "done" && sp.get("session_id")) {
      api.get(`/jobs/boost/status/${sp.get("session_id")}`).finally(()=>load());
    }
  }, []);

  if (err) return <div className="max-w-3xl mx-auto p-10"><p className="text-sm text-[#B84B4B]">{err}</p></div>;
  if (!j) return <div className="max-w-3xl mx-auto p-10 text-[#8A8A8A]">Loading…</div>;

  const remove = async () => {
    if (!window.confirm("Delete this job post? Applicants will lose access.")) return;
    try { await api.delete(`/jobs/${j.id}`); nav("/jobs/mine"); }
    catch (e) { setErr(formatErr(e)); }
  };

  const toggleStatus = async () => {
    const next = j.status === "open" ? "closed" : "open";
    try { await api.patch(`/jobs/${j.id}`, { status: next }); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10" data-testid="job-view">
      <Link to="/jobs" className="text-sm text-[#4A4A4A] inline-flex items-center gap-1 mb-5 hover:text-[#0A0A0A]"><ArrowLeft size={14}/> Back to jobs</Link>

      <div className="flex items-start gap-4">
        <img src={j.employer?.avatar} alt="" className="h-14 w-14 rounded-full object-cover bg-[#E5E5E3]"/>
        <div className="flex-1">
          <p className="overline" data-testid="job-category">{j.category} · {employmentLabel(j.employment_type)}</p>
          <h1 className="h-display text-3xl sm:text-4xl mt-1" data-testid="job-title">{j.title}</h1>
          <p className="text-[#4A4A4A] mt-1">
            <Link to={`/u/${j.employer_id}`} className="font-medium text-[#0A0A0A] hover:underline">{j.company_name || j.employer?.name}</Link>
            {j.employer?.verified && <BadgeCheck size={13} className="inline ml-1 -mt-0.5 text-[#0A0A0A]"/>}
            <span className="mx-2 opacity-50">·</span>
            <MapPin size={12} className="inline -mt-0.5"/> {j.location || "Remote"}
            {j.remote_ok && <> <span className="opacity-50">·</span> <Globe size={12} className="inline -mt-0.5"/> Remote OK</>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-medium" data-testid="job-salary">{formatSalary(j)}</p>
          <p className="text-xs text-[#8A8A8A] mt-1 inline-flex items-center gap-1"><Sparkles size={10}/> {j.application_count} applied</p>
          {j.is_boosted && (
            <p className="text-xs text-[#0057FF] mt-1 inline-flex items-center gap-1"><Flame size={10}/> {j.boost_tier}</p>
          )}
        </div>
      </div>

      <div className="card p-6 mt-6">
        <p className="whitespace-pre-wrap text-[#0A0A0A]" data-testid="job-description">{j.description}</p>
        {j.apply_questions?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#E5E5E3]">
            <p className="overline mb-2">Application questions</p>
            <ol className="list-decimal pl-5 text-sm text-[#4A4A4A] space-y-1.5">
              {j.apply_questions.map((q, i) => <li key={i}>{q}</li>)}
            </ol>
          </div>
        )}
      </div>

      {/* Owner controls */}
      {j.is_owner && (
        <div className="card p-5 mt-4 flex flex-wrap items-center gap-2" data-testid="job-owner-controls">
          <p className="text-sm text-[#4A4A4A] flex-1">
            You posted this · status: <span className="font-medium text-[#0A0A0A]">{j.status}</span>
          </p>
          <Link to={`/jobs/mine#${j.id}`} className="btn-ghost text-sm" data-testid="job-view-apps">
            <Sparkles size={13}/> {j.application_count} applications
          </Link>
          <button onClick={()=>setBoostOpen(true)} className="btn-secondary !py-2 !px-3 text-sm" data-testid="job-boost-open">
            <Flame size={13}/> Boost
          </button>
          <button onClick={toggleStatus} className="btn-ghost text-sm" data-testid="job-toggle-status">
            <Briefcase size={13}/> {j.status === "open" ? "Close hiring" : "Reopen"}
          </button>
          <Link to={`/jobs/new?edit=${j.id}`} className="btn-ghost text-sm" data-testid="job-edit"><Edit3 size={13}/> Edit</Link>
          <button onClick={remove} className="btn-ghost text-sm text-[#B84B4B]" data-testid="job-delete"><Trash2 size={13}/> Delete</button>
        </div>
      )}

      {/* Apply CTA */}
      {!j.is_owner && (
        <div className="card p-5 mt-4 flex items-center justify-between" data-testid="job-apply-bar">
          {!user ? (
            <>
              <p className="text-sm text-[#4A4A4A]">Sign in to apply on Hoomanz.</p>
              <Link to="/login" className="btn-primary">Sign in</Link>
            </>
          ) : j.has_applied ? (
            <p className="inline-flex items-center gap-2 text-sm text-[#0A0A0A]" data-testid="job-already-applied">
              <CheckCircle2 size={16} className="text-[#0A0A0A]"/> You&apos;ve applied to this job.
            </p>
          ) : j.status !== "open" ? (
            <p className="text-sm text-[#8A8A8A]">This job is no longer accepting applications.</p>
          ) : (
            <>
              <p className="text-sm text-[#4A4A4A]">Ready to apply? Takes 2 minutes.</p>
              <button className="btn-primary" onClick={()=>setApplyOpen(true)} data-testid="job-apply-open">
                <Send size={14}/> Apply now
              </button>
            </>
          )}
        </div>
      )}

      {applyOpen && (
        <ApplyModal job={j} onClose={()=>setApplyOpen(false)} onApplied={()=>{ setApplyOpen(false); load(); }}/>
      )}
      {boostOpen && (
        <BoostModal job={j} tiers={tiers} onClose={()=>setBoostOpen(false)}/>
      )}
    </div>
  );
}

function ApplyModal({ job, onClose, onApplied }) {
  const [cover, setCover] = useState("");
  const [answers, setAnswers] = useState((job.apply_questions || []).map(() => ""));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/jobs/${job.id}/apply`, { cover_note: cover, answers });
      onApplied();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="job-apply-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()}
            className="bg-white rounded-3xl p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline">Apply on Hoomanz</p>
            <h3 className="font-heading text-xl mt-0.5">{job.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#8A8A8A] hover:text-[#0A0A0A]"><X size={16}/></button>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Cover note</span>
          <textarea required minLength={10} maxLength={4000} rows={5}
            className="input mt-1.5" placeholder="Why you, why this role, why now."
            value={cover} onChange={(e)=>setCover(e.target.value)} data-testid="job-cover-note"/>
          <span className="text-xs text-[#8A8A8A] mt-1 inline-block">{cover.length}/4000 — at least 10 chars</span>
        </label>
        {job.apply_questions?.map((q, i) => (
          <label key={i} className="block mt-4">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">{q}</span>
            <textarea required rows={2} className="input mt-1.5"
              value={answers[i]} onChange={(e)=>{
                const next = [...answers]; next[i] = e.target.value; setAnswers(next);
              }} data-testid={`job-answer-${i}`}/>
          </label>
        ))}
        {err && <p className="text-sm text-[#B84B4B] mt-3" data-testid="job-apply-error">{err}</p>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button className="btn-primary flex-1" disabled={busy} data-testid="job-apply-submit">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Submitting…</> : <><Send size={14}/> Submit application</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function BoostModal({ job, tiers, onClose }) {
  const [picking, setPicking] = useState(null);
  const [err, setErr] = useState("");

  const buy = async (tier) => {
    setPicking(tier); setErr("");
    try {
      const { data } = await api.post(`/jobs/${job.id}/boost/checkout`,
        { tier, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); setPicking(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="job-boost-modal">
      <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl p-7 max-w-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline">Boost this job</p>
            <h3 className="font-heading text-xl mt-0.5">Get seen by more applicants.</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#8A8A8A] hover:text-[#0A0A0A]"><X size={16}/></button>
        </div>
        <div className="space-y-2">
          {Object.entries(tiers).map(([id, t]) => (
            <button key={id} onClick={()=>buy(id)} disabled={!!picking}
                    data-testid={`job-boost-tier-${id}`}
                    className="w-full card p-4 text-left hover:border-[#0A0A0A] transition-colors">
              <div className="flex items-center gap-3">
                <Flame size={16} className="text-[#0057FF]"/>
                <div className="flex-1">
                  <p className="font-medium">{t.label}</p>
                  <p className="text-xs text-[#4A4A4A]">{t.blurb}</p>
                </div>
                <p className="font-medium">${t.price}</p>
                {picking === id && <Loader2 size={14} className="animate-spin"/>}
              </div>
            </button>
          ))}
        </div>
        {err && <p className="text-sm text-[#B84B4B] mt-3" data-testid="job-boost-error">{err}</p>}
        <p className="text-xs text-[#8A8A8A] mt-4">Stripe Checkout · Cancel anytime · 2 % Hoomanz Tip added on top</p>
      </div>
    </div>
  );
}
